import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { SKILLS_VISIBLE_COUNT, formatDate, getDaysFromToday } from "./candidateHelpers";

const parseSkills = (skills) => {
    if (!skills) return [];
    if (typeof skills === "string") return skills.split(",").map((s) => s.trim()).filter(Boolean);
    return skills;
};

const SkillsCell = ({ skills = null, secondarySkills = null }) => {
    const primaryList = parseSkills(skills);
    const secondaryList = parseSkills(secondarySkills);
    const [popupPos, setPopupPos] = useState(null);
    const triggerRef = useRef(null);

    if (!primaryList.length && !secondaryList.length) return <span className="cv-na-text">N/A</span>;

    const visible = primaryList.slice(0, SKILLS_VISIBLE_COUNT);
    const extra = [...primaryList.slice(SKILLS_VISIBLE_COUNT), ...secondaryList];

    const showPopup = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };

    const hidePopup = () => setPopupPos(null);

    return (
        <div className="cv-skills-wrap">
            {visible.map((skill, idx) => (
                <span className="cv-skill-chip" key={idx}>{skill}</span>
            ))}
            {extra.length > 0 && (
                <>
                    <span
                        ref={triggerRef}
                        className="cv-skill-chip cv-more-chip"
                        onMouseEnter={showPopup}
                        onMouseLeave={hidePopup}
                    >
                        +{extra.length} more
                    </span>
                    {popupPos && ReactDOM.createPortal(
                        <div
                            className="cv-skills-popup"
                            style={{ top: popupPos.top, left: popupPos.left }}
                        >
                            {extra.map((s, i) => (
                                <span key={i}>{s}{i < extra.length - 1 && ", "}</span>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

const LIST_VISIBLE_COUNT = 2;

const ListsCell = ({ lists = null }) => {
    const listArray = Array.isArray(lists) ? lists : (lists ? [lists] : []);
    const [popupPos, setPopupPos] = useState(null);
    const triggerRef = useRef(null);

    if (!listArray.length) return <span className="cv-na-text">N/A</span>;

    const visible = listArray.slice(0, LIST_VISIBLE_COUNT);
    const extra = listArray.slice(LIST_VISIBLE_COUNT);

    const showPopup = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };

    const hidePopup = () => setPopupPos(null);

    return (
        <div className="cv-skills-wrap">
            {visible.map((item, idx) => (
                <span className="cv-list-chip" key={item.id || idx}>{item.name}</span>
            ))}
            {extra.length > 0 && (
                <>
                    <span
                        ref={triggerRef}
                        className="cv-list-chip cv-more-chip"
                        onMouseEnter={showPopup}
                        onMouseLeave={hidePopup}
                    >
                        +{extra.length} more
                    </span>
                    {popupPos && ReactDOM.createPortal(
                        <div
                            className="cv-skills-popup"
                            style={{ top: popupPos.top, left: popupPos.left }}
                        >
                            {extra.map((item, i) => (
                                <span key={item.id || i}>{item.name}{i < extra.length - 1 && ", "}</span>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

const CandidateTableBody = ({ candidates = [], onViewSummary, onDownload, onAddClick }) => {
    if (candidates.length === 0) {
        return (
            <tr>
                <td colSpan={7} className="td-empty">
                    No candidates yet. Click{" "}
                    <strong className="cv-empty-link" onClick={onAddClick}>
                        Add Candidates
                    </strong>{" "}
                    to get started.
                </td>
            </tr>
        );
    }

    return candidates.map((c) => (
        <tr key={c.id}>
            <td className="td-name">
                <span className="cv-candidate-name">{c.full_name}</span>
                <span className="cv-candidate-phone">{c.phone_number}</span>
            </td>
            <td className="td-center">{c.email}</td>
            <td className="td-center">{c.current_designation || "N/A"}</td>
            <td className="td-center">
                <div className="cv-days-cell">
                    <strong>{getDaysFromToday(c.created_at)}</strong>
                    <span>{formatDate(c.created_at)}</span>
                </div>
            </td>
            <td><SkillsCell skills={c.primary_skills} secondarySkills={c.secondary_skills} /></td>
            <td className="td-center">
                <ListsCell lists={c.lists} />
            </td>
            <td className="td-actions">
                <div className="cv-action-btns">
                    <button
                        className="cv-action-btn"
                        title="View Resume Summary"
                        onClick={() => onViewSummary(c)}
                    >
                        <img src="/images/eye-black-icon.svg" alt="View" width={16} height={16} />
                    </button>
                    <button
                        className="cv-action-btn"
                        title="Download Resume"
                        disabled={!c.resume_url}
                        onClick={() => onDownload(c)}
                    >
                        <img src="/images/download.svg" alt="Download" width={16} height={16} />
                    </button>
                </div>
            </td>
        </tr>
    ));
};

export default CandidateTableBody;
